import {KYC_PHOTO_STATUSES, KYC_PRE_SIGNED_URL_UPLOAD_STATUSES} from '../enums/enums';
import {kycPhotoGetPresignedURL, kycPhotoVerify, kycUploadPhotoToS3} from './kycPhotoVerificationService';
import {NavigationProp} from "@react-navigation/native";
import AsyncStorage from "@react-native-async-storage/async-storage";
import { updateLinkedApplication } from '@app/store/slices/authSlice';
import { CONTEXT } from '@app/configs/constants';

interface PhotoUploadParams {
    userPhoto: string;
    userDetails: any;
    setCTALoader: (loading: boolean) => void;
    setShowSuccessCard: (show: boolean) => void;
    setShowFailureCard: (show: boolean) => void;
    setErrorDetails: (details: { title: string; message: string }) => void;
    setShowPhotoMismatch: (showPhotoMismatch: boolean) => void;
    setUserPhoto: (userPhoto: string) => void;
    setNextStepId: (nextStepId: string) => void;
    setStepStatusMap?: (stepStatusMap: any) => void;
    setApplicationState?: (applicationState: any) => void;
    setContextfromApi?: (context: any) => void;
}

const handlePresignedURLResponse = async (userDetails: any, imageName: string) => {
    const applicationIdStored = await AsyncStorage.getItem("application_id")
    const applicationId = applicationIdStored ?? userDetails.linkedApplications[0].applicationId
    try {
        const response = await kycPhotoGetPresignedURL({
            applicationId: applicationId,
            imageName,
            imageType: 'image/jpeg'
        });
        return response.data.stepResponseObject;
    } catch (error: any) {
        throw new Error(error?.data?.message);
    }
};

const handleS3Upload = async (preSignedURL: string, userPhoto: string) => {
    try {
        const response = await kycUploadPhotoToS3({
            preSignedURL,
            imageBody: userPhoto.replace(/^data:image\/[a-z]+;base64,/, "")
        });
        if (response !== KYC_PRE_SIGNED_URL_UPLOAD_STATUSES.SUCCESS) {
            throw new Error('Failed to upload image to S3');
        }
        return response;
    } catch (error: any) {
        throw new Error(error?.data?.message);
    }
};

const handlePhotoVerification = async (userDetails: any) => {
    const applicationIdStored = await AsyncStorage.getItem("application_id")
    const applicationId = applicationIdStored ?? userDetails.linkedApplications[0].applicationId
    try {
        const response = await kycPhotoVerify({
            applicationId: applicationId
        });
        if (response.status !== 200) {
            throw new Error(response.data?.message || 'Failed to verify photo');
        }
        return response.data;
    } catch (error: any) {
        throw new Error(error?.data?.message);
    }
};

export const handlePhotoUpload = async ({
    userPhoto,
    userDetails,
    setCTALoader,
    setShowSuccessCard,
    setShowFailureCard,
    setErrorDetails,
    setShowPhotoMismatch,
    setNextStepId,
    setStepStatusMap,
    setApplicationState,
    setContextfromApi
}: PhotoUploadParams) => {
    const applicationIdStored = await AsyncStorage.getItem("application_id")
    const applicationId = applicationIdStored ?? userDetails.linkedApplications[0].applicationId
    setCTALoader(true);
    try {
        const imageName = `${applicationId}_${userDetails.linkedBorrowerAccounts[0].accountHolderPhoneNumber}.txt`;
        const preSignedURL = await handlePresignedURLResponse(userDetails, imageName);
        await handleS3Upload(preSignedURL, userPhoto);
        const verificationResult = await handlePhotoVerification(userDetails);
        const status = verificationResult.stepResponseObject.status;

        if (status === KYC_PHOTO_STATUSES.SUCCESS) {
            setShowSuccessCard(true);
            setNextStepId(verificationResult.updatedApplicationObj.currentStepId)
            setStepStatusMap?.(verificationResult.updatedApplicationObj.stepStatusMap);
            setApplicationState?.(verificationResult.updatedApplicationObj.applicationState);
            updateLinkedApplication(verificationResult.updatedApplicationObj);
            if(verificationResult?.stepResponseObject?.context?.toLowerCase() === CONTEXT.PARTNER.toLowerCase()) {
                setContextfromApi && setContextfromApi(verificationResult?.stepResponseObject?.context);
            }
            return;
        } else if (status === KYC_PHOTO_STATUSES.DEVIATION) {
            setShowPhotoMismatch(true)
        }  else if (status === KYC_PHOTO_STATUSES.REJECTED) {
            setShowFailureCard(true);
            setErrorDetails({
                title: verificationResult.stepResponseObject?.error?.errorTitle,
                message: verificationResult.stepResponseObject?.error?.errorMessage
            });
            return;
        }
    } catch (error: any) {
        setShowFailureCard(true);
        setErrorDetails({
            title: 'Photo verification failed',
            message: error.message || 'An unexpected error occurred.'
        });
    } finally {
        setCTALoader(false);
    }
};